import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import type { PublicSettings } from "@/types";
import { FalconLogo } from "@/components/ui/FalconLogo";
import clsx from "clsx";

interface NavSlotItem {
  id: string;
  slot: string;
  label: string;
  href: string;
  icon?: string;
  position: number;
  children: Array<{ title: string; path: string; slug: string }>;
}

export function SiteHeader() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { data: nav = [] } = useQuery<NavSlotItem[]>({
    queryKey: ["nav-header-slot"],
    queryFn: () => apiGet<NavSlotItem[]>(
      "/public/navigation/slots?slot=header&locale=de",
    ),
    // Refetch on mount + window focus so an admin saving a label in
    // another tab sees the new menu name as soon as they switch back.
    staleTime: 30_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => apiGet<PublicSettings>("/public/settings"),
    staleTime: 10 * 60_000,
  });

  useEffect(() => { if (searchOpen) inputRef.current?.focus(); }, [searchOpen]);

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    if (q.trim().length === 0) return;
    setSearchOpen(false);
    navigate("/suche?q=" + encodeURIComponent(q.trim()));
  }

  const siteName = settings?.site_name ?? "Falkenrehde";
  const tagline = settings?.site_tagline ?? "zu Hause im Havelland";

  return (
    <header className="sticky top-0 z-40 bg-bg/95 backdrop-blur border-b border-border shadow-soft">
      <div className="max-w-content mx-auto px-4 lg:px-6 h-20 flex items-center gap-6">
        <Link to="/" className="flex items-center gap-3" aria-label={siteName + " - Startseite"}>
          <FalconLogo size={44} decorative />
          <span className="leading-tight">
            <span className="block font-serif text-2xl font-bold text-primary tracking-tight">{siteName}</span>
            <span className="hidden sm:block text-xs text-text-muted italic">{tagline}</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-auto" aria-label="Hauptnavigation">
          {nav.map((n) => <SlotMenuItem key={n.id} node={n} />)}
        </nav>

        <div className="flex items-center gap-1 ml-auto lg:ml-2">
          <button aria-label={"Suche"}
                  className="p-2 rounded-md hover:bg-bg-muted text-text-muted hover:text-primary transition"
                  onClick={() => setSearchOpen((s) => !s)}>
            <SearchIcon />
          </button>
          <button aria-label={open ? "Menü schließen" : "Menü öffnen"}
                  className="lg:hidden p-2 rounded-md hover:bg-bg-muted text-text-muted hover:text-primary transition"
                  onClick={() => setOpen((o) => !o)}>
            {open ? <CloseIcon /> : <BurgerIcon />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <form onSubmit={submitSearch} className="border-t border-border bg-bg-soft">
          <div className="max-w-content mx-auto px-4 lg:px-6 py-3 flex items-center gap-2">
            <input ref={inputRef} type="search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder={"Suchbegriff eingeben…"}
                   className="flex-1 border border-border rounded-md px-3 py-2 focus:border-primary outline-none bg-bg" />
            <button type="submit" className="btn-primary">{"Suche"}</button>
          </div>
        </form>
      )}

      {open && (
        <nav className="lg:hidden border-t border-border bg-bg" aria-label="Mobile Navigation">
          <div className="max-w-content mx-auto px-4 py-3 flex flex-col">
            {nav.map((n) => (
              n.children.length > 0 ? (
                <details key={n.id} className="py-1 border-b border-border">
                  <summary className="py-2.5 cursor-pointer flex items-center justify-between">
                    <span>{n.label}</span>
                    <ChevronDown size={12} />
                  </summary>
                  <div className="pl-4 flex flex-col pb-2">
                    <NavLink to={n.href} onClick={() => setOpen(false)} className="py-1.5 text-text-muted">{n.label}</NavLink>
                    {n.children.map((c) => (
                      <NavLink key={c.slug} to={c.path} onClick={() => setOpen(false)} className="py-1.5">{c.title}</NavLink>
                    ))}
                  </div>
                </details>
              ) : (
                <NavLink key={n.id} to={n.href} onClick={() => setOpen(false)} className="py-2.5 border-b border-border">
                  {n.label}
                </NavLink>
              )
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function navLinkClass({ isActive }: { isActive: boolean }) {
  return clsx(
    "inline-flex items-center leading-6 px-3 py-2 rounded-md text-sm font-medium transition border-b-2 hover:bg-bg-muted align-middle",
    isActive ? "text-primary border-accent" : "text-text border-transparent",
  );
}

function SlotMenuItem({ node }: { node: NavSlotItem }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);
  function scheduleClose() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 220);
  }
  function cancelClose() {
    if (closeTimer.current) { window.clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  }
  const external = /^https?:\/\//i.test(node.href);
  if (external) {
    return (
      <a href={node.href} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center leading-6 px-3 py-2 rounded-md text-sm font-medium hover:bg-bg-muted transition text-text border-b-2 border-transparent align-middle">
        {node.label}
      </a>
    );
  }
  return (
    <div className="relative"
         onMouseEnter={cancelClose} onMouseLeave={scheduleClose}
         onFocus={cancelClose} onBlur={scheduleClose}>
      <NavLink to={node.href} end={node.href === "/"} className={navLinkClass}>
        {node.label}
        {node.children.length > 0 && <span className="ml-1 text-text-muted"><ChevronDown size={10} /></span>}
      </NavLink>
      {node.children.length > 0 && open && (
        <div className="absolute left-0 top-full z-50"
             onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
          <div aria-hidden style={{ height: 10 }} />
          <div className="bg-bg border border-border rounded-md shadow-card min-w-[260px] py-2">
            <NavLink to={node.href} className="block px-4 py-2 text-sm font-semibold text-primary hover:bg-bg-muted">
              {node.label}
            </NavLink>
            <div className="my-1 border-t border-border" />
            {node.children.map((c) => (
              <NavLink key={c.slug} to={c.path} className="block px-4 py-2 text-sm hover:bg-bg-muted">
                {c.title}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ChevronDown({ size }: { size: number }) {
  return (
    <svg aria-hidden width={size} height={size} viewBox="0 0 12 12" className="inline-block align-middle">
      <path d="M2 4l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
function BurgerIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M3 6h18M3 12h18M3 18h18" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
